import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const STAGES = ['Prospect','Qualified','Proposal','Negotiation','Closed Won','Closed Lost'];
const SC = {Prospect:'#94a3b8',Qualified:'#60a5fa',Proposal:'#a78bfa',Negotiation:'#f59e0b','Closed Won':'#34d399','Closed Lost':'#f87171'};
const SB = {Prospect:'#1e293b',Qualified:'#1e3a5f',Proposal:'#2e1f5e',Negotiation:'#431407','Closed Won':'#064e3b','Closed Lost':'#450a0a'};
const PC = ['#a78bfa','#34d399','#f59e0b','#60a5fa','#f472b6','#fb923c'];
const PE = ['🔬','🌿','🏛️','⚡','🎯','🚀','💡','🔷'];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt = v => (v||v===0) ? '$'+Number(v).toLocaleString() : '—';
const todayStr = () => new Date().toISOString().slice(0,10);
const isOverdue = d => d && d < todayStr();
const initials = n => n?.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)||'?';
const weekEnd = () => { const d=new Date(); d.setDate(d.getDate()+7); return d.toISOString().slice(0,10); };

function useIsMobile() {
  const [m,setM] = useState(window.innerWidth<768);
  useEffect(()=>{ const f=()=>setM(window.innerWidth<768); window.addEventListener('resize',f); return ()=>window.removeEventListener('resize',f); },[]);
  return m;
}

async function askClaude(prompt, system='You are a sharp business advisor. Be concise and direct.') {
  try {
    const res = await fetch('/.netlify/functions/claude', {
      method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ prompt, system })
    });
    const d = await res.json();
    return d.text || 'No response.';
  } catch { return 'AI unavailable — check connection.'; }
}

const EMPTY_DEAL = {name:'',stage:'Prospect',value:'',type:'one-off',mrr:'',contact:'',contact_title:'',email:'',notes:'',context:''};
const EMPTY_PROJECT = {name:'',color:PC[0],emoji:PE[0],description:'',strategy:'',milestones:[]};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Barlow:wght@700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  html{background:#0a0f1a;}
  html,body{margin:0;padding:0;height:100%;overflow:hidden;background:#0a0f1a;}
  #root{height:100%;overflow:hidden;}
  .app-header{position:fixed;top:0;left:0;right:0;z-index:100;background:#0a0f1a;border-bottom:1px solid #1e293b;padding-top:env(safe-area-inset-top);}
  .app-body{position:fixed;left:0;right:0;overflow-y:auto;-webkit-overflow-scrolling:touch;}
  .app-footer{position:fixed;bottom:0;left:0;right:0;z-index:100;background:#0a0f1a;border-top:1px solid #1e293b;padding-bottom:env(safe-area-inset-bottom);}
  ::-webkit-scrollbar{width:3px;}::-webkit-scrollbar-track{background:#0a0f1a;}::-webkit-scrollbar-thumb{background:#1e293b;border-radius:4px;}
  input,textarea,select{font-family:inherit;}
  .row{transition:background 0.1s;cursor:pointer;-webkit-tap-highlight-color:transparent;}
  .row:hover{background:#131c2e!important;}
  .row:active{background:#1a2540!important;}
  .bp{background:#3b82f6;color:#fff;border:none;padding:13px 18px;border-radius:12px;font-size:15px;font-weight:600;cursor:pointer;width:100%;font-family:inherit;transition:background 0.15s;}
  .bp:hover{background:#2563eb;}
  .bg{background:transparent;color:#94a3b8;border:1px solid #1e293b;padding:12px 16px;border-radius:12px;font-size:14px;cursor:pointer;font-family:inherit;width:100%;transition:border 0.15s;}
  .bg:hover{border-color:#3b82f6;color:#e2e8f0;}
  .bd{background:transparent;color:#f87171;border:1px solid #450a0a;padding:12px 16px;border-radius:12px;font-size:14px;cursor:pointer;font-family:inherit;width:100%;}
  .bd:hover{background:#450a0a;}
  input[type=text],input[type=email],input[type=number],input[type=date],input[type=password],textarea,select{background:#0f172a;border:1px solid #1e293b;color:#e2e8f0;border-radius:12px;padding:12px 14px;font-size:15px;width:100%;outline:none;transition:border 0.15s;-webkit-appearance:none;appearance:none;}
  input:focus,textarea:focus,select:focus{border-color:#3b82f6;}
  textarea{resize:vertical;min-height:90px;line-height:1.6;}
  select option{background:#0f172a;}
  .fi{animation:fi 0.18s ease;}
  @keyframes fi{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
  .lbl{font-size:11px;color:#64748b;display:block;margin-bottom:7px;text-transform:uppercase;letter-spacing:0.08em;font-weight:600;}
  .tag{display:inline-block;font-size:10px;padding:2px 8px;border-radius:8px;font-weight:600;}
  .card{background:#111827;border:1px solid #1e293b;border-radius:14px;padding:16px;}
  .nb{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;background:none;border:none;color:#64748b;font-size:10px;font-family:inherit;cursor:pointer;padding:8px 0;flex:1;transition:color 0.15s;-webkit-tap-highlight-color:transparent;position:relative;}
  .nb.on{color:#60a5fa;}
  h1{font-family:'Barlow',sans-serif;font-weight:800;letter-spacing:-0.5px;}
  h2{font-family:'Barlow',sans-serif;font-weight:700;letter-spacing:-0.3px;}
  .toggle-pill{display:flex;background:#111827;border:1px solid #1e293b;border-radius:10px;padding:3px;gap:2px;}
  .toggle-btn{background:transparent;color:#64748b;border:1px solid transparent;border-radius:8px;padding:6px 14px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit;transition:all 0.15s;white-space:nowrap;}
  .toggle-btn.on{background:#1e3a5f;color:#60a5fa;border-color:#3b82f655;}
`;

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email,setEmail] = useState('');
  const [password,setPassword] = useState('');
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setError(error.message); setLoading(false); }
  }

  return (
    <div style={{background:'#0a0f1a',minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Inter','Segoe UI',sans-serif",padding:20}}>
      <style>{CSS}</style>
      <div style={{width:'100%',maxWidth:360}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:32,justifyContent:'center'}}>
          <div style={{width:36,height:36,background:'linear-gradient(135deg,#3b82f6,#8b5cf6)',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>◈</div>
          <h1 style={{fontSize:24,color:'#f8fafc'}}>DealFlow</h1>
        </div>
        <form onSubmit={handleLogin} style={{display:'flex',flexDirection:'column',gap:14}}>
          <div><span className="lbl">Email</span><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" required/></div>
          <div><span className="lbl">Password</span><input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required/></div>
          {error && <div style={{fontSize:13,color:'#f87171',background:'#450a0a',padding:'10px 14px',borderRadius:10}}>{error}</div>}
          <button className="bp" type="submit" disabled={loading}>{loading?'Signing in…':'Sign In'}</button>
        </form>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session); setAuthChecked(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_,session) => setSession(session));
    return () => subscription.unsubscribe();
  }, []);

  if (!authChecked) return <div style={{background:'#0a0f1a',minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:'#60a5fa',fontFamily:'monospace'}}>Loading…</div>;
  if (!session) return <LoginScreen onLogin={setSession}/>;
  return <Dashboard session={session}/>;
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ session }) {
  const isMobile = useIsMobile();
  const [projects, setProjects] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pipeline');
  const [pView, setPView] = useState('list');
  const [activeProject, setActiveProject] = useState(null);
  const [activeDeal, setActiveDeal] = useState(null);
  const [dealForm, setDealForm] = useState(EMPTY_DEAL);
  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT);

  // ── Load all data
  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const [{ data: p }, { data: d }, { data: t }, { data: a }] = await Promise.all([
      supabase.from('projects').select('*').order('sort_order'),
      supabase.from('deals').select('*').order('sort_order'),
      supabase.from('tasks').select('*').order('sort_order'),
      supabase.from('activity').select('*').order('created_at', { ascending: false }),
    ]);
    setProjects(p||[]); setDeals(d||[]); setTasks(t||[]); setActivity(a||[]);
    setLoading(false);
  }

  // ── Export data
  function exportData() {
    const data = {
      version: 4, exportedAt: new Date().toISOString(),
      projects, deals,
      tasks, activity
    };
    const blob = new Blob([JSON.stringify(data,null,2)],{type:'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`dealflow-export-${todayStr()}.json`; a.click();
    URL.revokeObjectURL(url);
  }

  // ── Import data
  async function importData(e) {
    const file = e.target.files?.[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data.projects || !data.deals) { alert('Invalid file'); return; }
        // Upsert everything
        await supabase.from('projects').upsert(data.projects);
        await supabase.from('deals').upsert(data.deals);
        if (data.tasks) await supabase.from('tasks').upsert(data.tasks);
        if (data.activity) await supabase.from('activity').upsert(data.activity);
        await loadAll();
      } catch { alert('Import failed — invalid file'); }
    };
    reader.readAsText(file);
  }

  // ── Project CRUD
  async function saveProjectForm() {
    if (!projectForm.name.trim()) return;
    const payload = {
      name: projectForm.name, color: projectForm.color, emoji: projectForm.emoji,
      description: projectForm.description, strategy: projectForm.strategy,
      milestones: projectForm.milestones || []
    };
    if (pView === 'addProject') {
      const { data } = await supabase.from('projects').insert(payload).select().single();
      setProjects(prev => [...prev, data]);
      setActiveProject(data); setPView('detail');
    } else {
      const { data } = await supabase.from('projects').update(payload).eq('id', activeProject.id).select().single();
      setProjects(prev => prev.map(p => p.id===data.id ? data : p));
      setActiveProject(data); setPView('detail');
    }
  }

  async function deleteProject(id) {
    await supabase.from('projects').delete().eq('id', id);
    setProjects(prev => prev.filter(p => p.id !== id));
    setDeals(prev => prev.filter(d => d.project_id !== id));
    setActiveProject(null); setPView('list');
  }

  async function addMilestone(text) {
    if (!text.trim()) return;
    const milestones = [...(activeProject.milestones||[]), {id: Date.now(), text: text.trim(), done: false}];
    const { data } = await supabase.from('projects').update({ milestones }).eq('id', activeProject.id).select().single();
    setProjects(prev => prev.map(p => p.id===data.id ? data : p));
    setActiveProject(data);
  }

  async function toggleMilestone(mid) {
    const milestones = activeProject.milestones.map(m => m.id===mid ? {...m, done: !m.done} : m);
    const { data } = await supabase.from('projects').update({ milestones }).eq('id', activeProject.id).select().single();
    setProjects(prev => prev.map(p => p.id===data.id ? data : p));
    setActiveProject(data);
  }

  async function deleteMilestone(mid) {
    const milestones = activeProject.milestones.filter(m => m.id !== mid);
    const { data } = await supabase.from('projects').update({ milestones }).eq('id', activeProject.id).select().single();
    setProjects(prev => prev.map(p => p.id===data.id ? data : p));
    setActiveProject(data);
  }

  // ── Deal CRUD
  async function saveDealForm() {
    if (!dealForm.name.trim()) return;
    const payload = {
      name: dealForm.name, stage: dealForm.stage,
      value: Number(dealForm.value)||0, type: dealForm.type,
      mrr: Number(dealForm.mrr)||0, contact: dealForm.contact,
      contact_title: dealForm.contact_title, email: dealForm.email,
      notes: dealForm.notes, context: dealForm.context,
      project_id: activeProject.id
    };
    if (pView === 'addDeal') {
      const { data } = await supabase.from('deals').insert(payload).select().single();
      setDeals(prev => [data, ...prev]);
      setActiveDeal(data); setPView('dealDetail');
    } else {
      const { data } = await supabase.from('deals').update(payload).eq('id', activeDeal.id).select().single();
      setDeals(prev => prev.map(d => d.id===data.id ? data : d));
      setActiveDeal(data); setPView('dealDetail');
    }
  }

  async function deleteDeal(id) {
    await supabase.from('deals').delete().eq('id', id);
    setDeals(prev => prev.filter(d => d.id !== id));
    setTasks(prev => prev.filter(t => t.deal_id !== id));
    setActivity(prev => prev.filter(a => a.deal_id !== id));
    setActiveDeal(null); setPView('detail');
  }

  // ── Task CRUD
  async function addTask(dealId, text, dueDate) {
    if (!text.trim()) return;
    const { data } = await supabase.from('tasks').insert({ deal_id: dealId, text: text.trim(), due_date: dueDate||null }).select().single();
    setTasks(prev => [...prev, data]);
  }

  async function completeTask(dealId, taskId) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    await supabase.from('tasks').update({ done: true, done_at: new Date().toISOString() }).eq('id', taskId);
    setTasks(prev => prev.map(t => t.id===taskId ? {...t, done: true} : t));
    await addActivityEntry(dealId, `✓ Done: ${task.text}`, true);
  }

  async function deleteTask(taskId) {
    await supabase.from('tasks').delete().eq('id', taskId);
    setTasks(prev => prev.filter(t => t.id !== taskId));
  }

  // ── Activity
  async function addActivityEntry(dealId, text, isSystem=false) {
    const { data } = await supabase.from('activity').insert({ deal_id: dealId, text, is_system: isSystem }).select().single();
    setActivity(prev => [data, ...prev]);
  }

  // ── Computed
  const today = todayStr();
  const openTasks = tasks.filter(t => !t.done);
  const overdueCount = openTasks.filter(t => isOverdue(t.due_date)).length;
  const totalPipeline = deals.filter(d=>d.stage!=='Closed Lost').reduce((a,b)=>a+(Number(b.value)||0),0);
  const totalMRR = deals.filter(d=>d.type==='recurring'&&d.stage!=='Closed Lost').reduce((a,b)=>a+(Number(b.mrr)||0),0);
  const totalWon = deals.filter(d=>d.stage==='Closed Won').reduce((a,b)=>a+(Number(b.value)||0),0);
  const inProgress = deals.filter(d=>!['Closed Won','Closed Lost'].includes(d.stage)).length;
  const closedDeals = deals.filter(d=>['Closed Won','Closed Lost'].includes(d.stage)).length;
  const winRate = closedDeals ? Math.round(deals.filter(d=>d.stage==='Closed Won').length/closedDeals*100)+'%' : '—';

  const dealTasks = d => tasks.filter(t => t.deal_id === d.id);
  const dealActivity = d => activity.filter(a => a.deal_id === d.id);

  if (loading) return <div style={{background:'#0a0f1a',minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:'#60a5fa',fontFamily:'monospace'}}>Loading your data…</div>;

  const backLabel = {addDeal:'← Project',editDeal:'← Deal',dealDetail:activeProject?.name||'← Project',detail:'← Projects',editProject:'← Project',addProject:'← Projects'};

  return (
    <div style={{background:'#0a0f1a',fontFamily:"'Inter','Segoe UI',sans-serif",color:'#e2e8f0'}}>
      <style>{CSS}</style>

      {/* HEADER - fixed at top */}
      <div className="app-header">
        <div style={{padding:'12px 18px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          {tab==='projects'&&pView!=='list'&&(
            <button onClick={()=>{
              if(['dealDetail','addDeal','editDeal'].includes(pView)) setPView('detail');
              else { setPView('list'); setActiveProject(null); setActiveDeal(null); }
            }} style={{background:'none',border:'none',color:'#60a5fa',fontSize:13,cursor:'pointer',fontFamily:'inherit',fontWeight:600,padding:'6px 10px 6px 0'}}>
              {backLabel[pView]||'← Back'}
            </button>
          )}
          {(tab!=='projects'||pView==='list')&&(
            <div style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}} onClick={()=>setTab('pipeline')}>
              <div style={{width:28,height:28,background:'linear-gradient(135deg,#3b82f6,#8b5cf6)',borderRadius:7,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>◈</div>
              <h1 style={{fontSize:17,color:'#f8fafc'}}>DealFlow</h1>
            </div>
          )}
          {tab==='projects'&&pView!=='list'&&(
            <h1 style={{fontSize:16,color:'#f8fafc'}}>
              {pView==='detail'?activeProject?.name:pView==='dealDetail'?activeDeal?.name:pView==='addDeal'?'New Deal':pView==='editDeal'?'Edit Deal':pView==='addProject'?'New Project':'Edit Project'}
            </h1>
          )}
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {(tab==='pipeline'||(tab==='projects'&&pView==='list'))&&(
            <button onClick={()=>{setProjectForm(EMPTY_PROJECT);setTab('projects');setPView('addProject');}} style={{background:'#1e3a5f',border:'1px solid #3b82f655',color:'#60a5fa',padding:'7px 14px',borderRadius:9,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>+ Project</button>
          )}
          {tab==='projects'&&pView==='detail'&&activeProject&&(<>
            <button onClick={()=>{setDealForm(EMPTY_DEAL);setPView('addDeal');}} style={{background:activeProject.color+'22',border:`1px solid ${activeProject.color}55`,color:activeProject.color,padding:'7px 14px',borderRadius:9,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>+ Deal</button>
            <button onClick={()=>{setProjectForm({...activeProject});setPView('editProject');}} style={{background:'none',border:'none',color:'#475569',cursor:'pointer',fontSize:18,padding:'4px'}}>⚙</button>
          </>)}
          {tab==='projects'&&pView==='dealDetail'&&activeDeal&&(
            <button onClick={()=>{setDealForm({...activeDeal,contact_title:activeDeal.contact_title||''});setPView('editDeal');}} style={{background:'none',border:'1px solid #1e293b',color:'#94a3b8',padding:'6px 14px',borderRadius:9,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Edit</button>
          )}
          <button onClick={()=>supabase.auth.signOut()} style={{background:'none',border:'none',color:'#334155',cursor:'pointer',fontSize:12,fontFamily:'inherit',padding:'4px 8px'}}>Sign out</button>
        </div>
        </div>
      </div>

      {/* BODY - scrollable middle */}
      <div className="app-body" style={{top:'calc(env(safe-area-inset-top) + 53px)',bottom:'calc(env(safe-area-inset-bottom) + 56px)'}}>

        {/* PIPELINE */}
        {tab==='pipeline'&&(
          <div className="fi" style={{padding:'20px 18px',maxWidth:640,margin:'0 auto'}}>
            <h1 style={{fontSize:26,color:'#f8fafc',marginBottom:20}}>Pipeline Overview</h1>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
              {[{label:'TOTAL PIPELINE',value:fmt(totalPipeline)},{label:'CLOSED WON',value:fmt(totalWon)},{label:'IN PROGRESS',value:String(inProgress)},{label:'WIN RATE',value:winRate}].map((s,i)=>(
                <div key={i} className="card"><div className="lbl">{s.label}</div><h1 style={{fontSize:26,color:'#f1f5f9'}}>{s.value}</h1></div>
              ))}
            </div>
            <div className="card" style={{marginBottom:20,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div><div className="lbl">MONTHLY RECURRING REVENUE</div><h1 style={{fontSize:26,color:'#34d399'}}>{fmt(totalMRR)}<span style={{fontSize:13,color:'#475569',fontFamily:'Inter',fontWeight:400}}>/mo</span></h1></div>
              <div style={{textAlign:'right'}}><div style={{fontSize:11,color:'#64748b'}}>{deals.filter(d=>d.type==='recurring'&&d.stage!=='Closed Lost').length} recurring</div><div style={{fontSize:11,color:'#475569',marginTop:3}}>{fmt(totalMRR*12)}/yr</div></div>
            </div>
            <h2 style={{fontSize:17,color:'#f1f5f9',marginBottom:12}}>By Stage</h2>
            {(()=>{
              const maxV=Math.max(...STAGES.map(s=>deals.filter(d=>d.stage===s).reduce((a,b)=>a+(Number(b.value)||0),0)),1);
              return STAGES.map(s=>{
                const sd=deals.filter(d=>d.stage===s); const sv=sd.reduce((a,b)=>a+(Number(b.value)||0),0);
                if(!sd.length) return null;
                return(<div key={s} className="card row" style={{marginBottom:10}} onClick={()=>{setTab('projects');setPView('list');}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}><span style={{width:9,height:9,borderRadius:'50%',background:SC[s],display:'inline-block'}}/><span style={{fontSize:14,fontWeight:600,color:'#e2e8f0'}}>{s}</span><span style={{fontSize:12,color:'#64748b'}}>{sd.length}</span></div>
                    <span style={{fontSize:14,fontWeight:700,color:SC[s]}}>{fmt(sv)}</span>
                  </div>
                  <div style={{height:5,background:'#1e293b',borderRadius:4,overflow:'hidden'}}><div style={{height:'100%',width:`${sv/maxV*100}%`,background:SC[s],borderRadius:4}}/></div>
                </div>);
              });
            })()}
            <h2 style={{fontSize:17,color:'#f1f5f9',marginBottom:12,marginTop:20}}>By Project</h2>
            {projects.map(p=>{
              const pd=deals.filter(d=>d.project_id===p.id&&d.stage!=='Closed Lost');
              const pV=pd.reduce((a,b)=>a+(Number(b.value)||0),0);
              const pM=pd.filter(d=>d.type==='recurring').reduce((a,b)=>a+(Number(b.mrr)||0),0);
              const pOv=pd.flatMap(d=>tasks.filter(t=>t.deal_id===d.id&&isOverdue(t.due_date)&&!t.done)).length;
              return(<div key={p.id} className="card row" style={{marginBottom:10,borderLeft:`4px solid ${p.color}`}} onClick={()=>{setActiveProject(p);setPView('detail');setTab('projects');}}>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:40,height:40,borderRadius:11,background:p.color+'22',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0}}>{p.emoji}</div>
                  <div style={{flex:1}}><div style={{fontWeight:700,fontSize:14,color:p.color}}>{p.name}</div><div style={{fontSize:12,color:'#64748b',marginTop:2}}>{pd.length} active{pM>0?` · ${fmt(pM)}/mo`:''}</div></div>
                  <div style={{textAlign:'right'}}><div style={{fontSize:15,fontWeight:700,color:'#f1f5f9'}}>{fmt(pV)}</div>{pOv>0&&<span className="tag" style={{marginTop:4,background:'#450a0a',color:'#f87171'}}>⚠ {pOv}</span>}</div>
                </div>
              </div>);
            })}
            <div style={{marginTop:28,paddingTop:20,borderTop:'1px solid #1e293b',display:'flex',gap:10}}>
              <button onClick={exportData} style={{flex:1,background:'#111827',border:'1px solid #1e293b',color:'#94a3b8',padding:'10px',borderRadius:10,fontSize:13,cursor:'pointer',fontFamily:'inherit',fontWeight:500}}>⬇ Export Data</button>
              <label style={{flex:1,background:'#111827',border:'1px solid #1e293b',color:'#94a3b8',padding:'10px',borderRadius:10,fontSize:13,cursor:'pointer',fontFamily:'inherit',fontWeight:500,textAlign:'center',display:'block'}}>
                ⬆ Import Data<input type="file" accept=".json" onChange={importData} style={{display:'none'}}/>
              </label>
            </div>
          </div>
        )}

        {/* PROJECTS */}
        {tab==='projects'&&(
          <div className="fi">
            {pView==='list'&&(
              <div style={{padding:'16px 18px',maxWidth:640,margin:'0 auto'}}>
                <h1 style={{fontSize:24,color:'#f8fafc',marginBottom:18}}>Projects</h1>
                {projects.map(p=>{
                  const pd=deals.filter(d=>d.project_id===p.id);
                  const pV=pd.filter(d=>d.stage!=='Closed Lost').reduce((a,b)=>a+(Number(b.value)||0),0);
                  const pM=pd.filter(d=>d.type==='recurring'&&d.stage!=='Closed Lost').reduce((a,b)=>a+(Number(b.mrr)||0),0);
                  const pOv=pd.flatMap(d=>tasks.filter(t=>t.deal_id===d.id&&isOverdue(t.due_date)&&!t.done)).length;
                  const ms=p.milestones||[]; const msDone=ms.filter(m=>m.done).length;
                  return(<div key={p.id} className="card row" style={{marginBottom:12,borderLeft:`4px solid ${p.color}`}} onClick={()=>{setActiveProject(p);setPView('detail');}}>
                    <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10}}>
                      <div style={{width:44,height:44,borderRadius:12,background:p.color+'22',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>{p.emoji}</div>
                      <div style={{flex:1}}><div style={{fontWeight:700,fontSize:15,color:p.color}}>{p.name}</div>{p.description&&<div style={{fontSize:12,color:'#64748b',marginTop:1}}>{p.description}</div>}</div>
                      <div style={{fontSize:18,color:'#334155'}}>›</div>
                    </div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      <span className="tag" style={{background:'#1e3a5f',color:'#60a5fa'}}>{fmt(pV)} pipeline</span>
                      {pM>0&&<span className="tag" style={{background:'#064e3b',color:'#34d399'}}>{fmt(pM)}/mo</span>}
                      {pOv>0&&<span className="tag" style={{background:'#450a0a',color:'#f87171'}}>⚠ {pOv} overdue</span>}
                      {ms.length>0&&<span className="tag" style={{background:'#2e1f5e',color:'#a78bfa'}}>{msDone}/{ms.length} milestones</span>}
                    </div>
                  </div>);
                })}
                {!projects.length&&<div style={{padding:40,textAlign:'center',color:'#475569',fontSize:14}}>No projects yet. Tap + Project to add one.</div>}
              </div>
            )}
            {pView==='detail'&&activeProject&&(
              <ProjectDetail
                project={activeProject}
                deals={deals.filter(d=>d.project_id===activeProject.id)}
                tasks={tasks} activity={activity}
                onSelectDeal={d=>{setActiveDeal(d);setPView('dealDetail');}}
                onToggleMilestone={toggleMilestone} onDeleteMilestone={deleteMilestone} onAddMilestone={addMilestone}
              />
            )}
            {pView==='dealDetail'&&activeDeal&&(
              <DealDetail
                deal={activeDeal} project={activeProject}
                tasks={dealTasks(activeDeal)} activity={dealActivity(activeDeal)}
                onEdit={()=>{setDealForm({...activeDeal});setPView('editDeal');}}
                onDelete={()=>deleteDeal(activeDeal.id)}
                onAddActivity={text=>addActivityEntry(activeDeal.id,text)}
                onCompleteTask={tid=>completeTask(activeDeal.id,tid)}
                onAddTask={(text,date)=>addTask(activeDeal.id,text,date)}
                onDeleteTask={deleteTask}
              />
            )}
            {(pView==='addDeal'||pView==='editDeal')&&(
              <div style={{padding:'16px 18px',maxWidth:580,margin:'0 auto'}}>
                <DealForm form={dealForm} setForm={setDealForm}/>
                <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:20}}>
                  <button className="bp" onClick={saveDealForm}>{pView==='addDeal'?'Save Deal':'Save Changes'}</button>
                  <button className="bg" onClick={()=>setPView(pView==='addDeal'?'detail':'dealDetail')}>Cancel</button>
                  {pView==='editDeal'&&<button className="bd" onClick={()=>deleteDeal(activeDeal.id)}>Delete Deal</button>}
                </div>
              </div>
            )}
            {(pView==='addProject'||pView==='editProject')&&(
              <ProjectForm form={projectForm} setForm={setProjectForm} isEdit={pView==='editProject'}
                onSave={saveProjectForm} onCancel={()=>setPView(pView==='addProject'?'list':'detail')}
                onDelete={pView==='editProject'?()=>deleteProject(activeProject.id):null}
                activeProject={activeProject} onAddMilestone={addMilestone} onToggleMilestone={toggleMilestone} onDeleteMilestone={deleteMilestone}
              />
            )}
          </div>
        )}

        {/* ACTIONS */}
        {tab==='actions'&&(
          <ActionsPanel
            deals={deals} projects={projects} tasks={tasks} activity={activity}
            onCompleteTask={completeTask}
            onSelectDeal={d=>{setActiveDeal(d);setActiveProject(projects.find(p=>p.id===d.project_id));setPView('dealDetail');setTab('projects');}}
            onAddTask={addTask}
          />
        )}

        {/* DIGEST */}
        {tab==='digest'&&<DigestPanel deals={deals} projects={projects} tasks={tasks}/>}
      </div>

      {/* BOTTOM NAV - fixed at bottom */}
      <div className="app-footer" style={{display:'flex'}}>
        {[
          {id:'pipeline',label:'Pipeline',icon:<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>},
          {id:'projects',label:'Projects',icon:<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M3 7a2 2 0 012-2h3l2 2h9a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/></svg>},
          {id:'actions',label:'Actions',badge:overdueCount,icon:<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>},
          {id:'digest',label:'Digest',icon:<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>},
        ].map(t=>(
          <button key={t.id} className={`nb ${tab===t.id?'on':''}`} onClick={()=>setTab(t.id)}>
            {t.badge>0&&<span style={{position:'absolute',top:6,right:'18%',minWidth:16,height:16,borderRadius:8,background:'#f87171',border:'2px solid #0a0f1a',fontSize:9,fontWeight:700,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',padding:'0 3px'}}>{t.badge}</span>}
            {t.icon}{t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── PROJECT DETAIL ───────────────────────────────────────────────────────────
function ProjectDetail({project,deals,tasks,onSelectDeal,onToggleMilestone,onDeleteMilestone,onAddMilestone}) {
  const [newMs,setNewMs]=useState('');
  const pipeline=deals.filter(d=>!['Closed Won','Closed Lost'].includes(d.stage)).reduce((a,b)=>a+(Number(b.value)||0),0);
  const mrr=deals.filter(d=>d.type==='recurring'&&d.stage!=='Closed Lost').reduce((a,b)=>a+(Number(b.mrr)||0),0);
  const won=deals.filter(d=>d.stage==='Closed Won').reduce((a,b)=>a+(Number(b.value)||0),0);
  const ms=project.milestones||[]; const msDone=ms.filter(m=>m.done).length;
  return(
    <div className="fi" style={{padding:'16px 18px',maxWidth:640,margin:'0 auto'}}>
      <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:20}}>
        <div style={{width:50,height:50,borderRadius:14,background:project.color+'22',border:`2px solid ${project.color}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,flexShrink:0}}>{project.emoji}</div>
        <div><h1 style={{fontSize:22,color:project.color}}>{project.name}</h1>{project.description&&<div style={{fontSize:13,color:'#64748b',marginTop:2}}>{project.description}</div>}</div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:18}}>
        {[{label:'PIPELINE',value:fmt(pipeline),c:'#60a5fa'},{label:'MRR',value:mrr>0?fmt(mrr)+'/mo':'—',c:'#34d399'},{label:'WON',value:fmt(won),c:'#a78bfa'}].map((s,i)=>(
          <div key={i} className="card" style={{padding:'12px 14px'}}><div className="lbl">{s.label}</div><h2 style={{fontSize:17,color:s.c}}>{s.value}</h2></div>
        ))}
      </div>
      <div style={{marginBottom:18}}>
        <h2 style={{fontSize:15,color:'#f1f5f9',marginBottom:10}}>Stage Breakdown</h2>
        <div style={{display:'flex',gap:3,marginBottom:6}}>
          {STAGES.slice(0,5).map(s=>{const c=deals.filter(d=>d.stage===s).length;return(<div key={s} style={{flex:1,textAlign:'center'}}><div style={{height:5,borderRadius:3,background:c>0?SC[s]:'#1e293b',marginBottom:4}}/><div style={{fontSize:9,color:c>0?SC[s]:'#334155',fontWeight:600}}>{c||''}</div></div>);})}
        </div>
        <div style={{display:'flex',gap:3}}>{STAGES.slice(0,5).map(s=><div key={s} style={{flex:1,fontSize:8,color:'#334155',textAlign:'center',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s}</div>)}</div>
      </div>
      {project.strategy&&<div className="card" style={{marginBottom:16}}><div className="lbl">Strategy</div><div style={{fontSize:14,color:'#94a3b8',lineHeight:1.7,whiteSpace:'pre-wrap'}}>{project.strategy}</div></div>}
      <div style={{marginBottom:18}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
          <h2 style={{fontSize:15,color:'#f1f5f9'}}>Milestones <span style={{fontSize:12,color:'#64748b',fontFamily:'Inter',fontWeight:400}}>{msDone}/{ms.length}</span></h2>
        </div>
        {ms.length>0&&<div style={{height:4,background:'#1e293b',borderRadius:4,overflow:'hidden',marginBottom:12}}><div style={{height:'100%',width:`${ms.length?msDone/ms.length*100:0}%`,background:project.color,borderRadius:4,transition:'width 0.4s'}}/></div>}
        {ms.map(m=>(
          <div key={m.id} style={{display:'flex',alignItems:'center',gap:10,marginBottom:8,padding:'10px 12px',background:'#111827',borderRadius:10}}>
            <input type="checkbox" checked={m.done} onChange={()=>onToggleMilestone(m.id)} style={{width:16,height:16,cursor:'pointer',accentColor:project.color,flexShrink:0}}/>
            <span style={{flex:1,fontSize:14,color:m.done?'#475569':'#cbd5e1',textDecoration:m.done?'line-through':'none'}}>{m.text}</span>
            <button onClick={()=>onDeleteMilestone(m.id)} style={{background:'none',border:'none',color:'#334155',cursor:'pointer',fontSize:16}}>✕</button>
          </div>
        ))}
        <div style={{display:'flex',gap:8,marginTop:8}}>
          <input type="text" value={newMs} onChange={e=>setNewMs(e.target.value)} placeholder="Add milestone…" style={{fontSize:13,padding:'10px 12px'}} onKeyDown={e=>{if(e.key==='Enter'&&newMs.trim()){onAddMilestone(newMs);setNewMs('');}}}/>
          <button onClick={()=>{if(newMs.trim()){onAddMilestone(newMs);setNewMs('');}}} style={{background:'#1e3a5f',border:'1px solid #3b82f655',color:'#60a5fa',borderRadius:10,padding:'10px 16px',cursor:'pointer',fontSize:13,fontFamily:'inherit',whiteSpace:'nowrap'}}>Add</button>
        </div>
      </div>
      <h2 style={{fontSize:15,color:'#f1f5f9',marginBottom:12}}>Deals <span style={{fontSize:12,color:'#64748b',fontFamily:'Inter',fontWeight:400}}>{deals.length}</span></h2>
      {!deals.length&&<div style={{padding:'16px 0',color:'#475569',fontSize:13}}>No deals yet.</div>}
      {deals.map(d=>{
        const dt=tasks.filter(t=>t.deal_id===d.id&&!t.done);
        const ovTasks=dt.filter(t=>isOverdue(t.due_date));
        return(
          <div key={d.id} className="card row" style={{marginBottom:10}} onClick={()=>onSelectDeal(d)}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:5}}>
                  <span style={{fontSize:14,fontWeight:600,color:'#f1f5f9'}}>{d.name}</span>
                  {ovTasks.length>0&&<span className="tag" style={{background:'#450a0a',color:'#f87171'}}>⚠ {ovTasks.length}</span>}
                  {d.type==='recurring'&&<span className="tag" style={{background:'#1e3a5f',color:'#60a5fa'}}>MRR</span>}
                </div>
                <div style={{fontSize:12,color:'#64748b'}}>{dt.length} open task{dt.length!==1?'s':''}{dt[0]?` · ${dt[0].text}`:''}</div>
              </div>
              <div style={{textAlign:'right',flexShrink:0}}>
                <div style={{fontSize:15,fontWeight:700,color:'#f1f5f9'}}>{fmt(d.value)}</div>
                <span className="tag" style={{marginTop:4,background:SB[d.stage],color:SC[d.stage]}}>{d.stage}</span>
              </div>
              <div style={{color:'#334155',fontSize:18}}>›</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── DEAL DETAIL ──────────────────────────────────────────────────────────────
function DealDetail({deal,project,tasks,activity,onEdit,onDelete,onAddActivity,onCompleteTask,onAddTask,onDeleteTask}) {
  const [actText,setActText]=useState('');
  const [taskText,setTaskText]=useState('');
  const [taskDate,setTaskDate]=useState('');
  const [showEmail,setShowEmail]=useState(false);
  const [coaching,setCoaching]=useState('');
  const [coachLoading,setCoachLoading]=useState(false);
  const [showDone,setShowDone]=useState(false);
  const openTasks=tasks.filter(t=>!t.done);
  const doneTasks=tasks.filter(t=>t.done);
  const idx=['Prospect','Qualified','Proposal','Negotiation','Closed Won'].indexOf(deal.stage);

  async function getCoaching() {
    setCoachLoading(true); setCoaching('');
    const tasksSummary=openTasks.map(t=>`- ${t.text}${t.due_date?` (due ${t.due_date})`:''}`).join('\n');
    const r=await askClaude(
      `Deal: "${deal.name}" | Project: ${project?.name} | Stage: ${deal.stage} | Value: $${deal.value} | Contact: ${deal.contact} (${deal.contact_title})\nOpen tasks:\n${tasksSummary||'none'}\nNotes: ${deal.notes||'none'}\nContext: ${deal.context||'none'}\nRecent activity: ${activity.slice(0,3).map(a=>a.text).join('; ')}\n\nGive me 3 sharp tactical next moves for this deal.`,
      'You are an elite sales strategist. Give 3 numbered, concrete, high-impact recommendations. No preamble.'
    );
    setCoaching(r); setCoachLoading(false);
  }

  return(
    <div className="fi" style={{padding:'16px 18px',maxWidth:600,margin:'0 auto'}}>
      <div style={{marginBottom:16}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
          <span className="tag" style={{background:SB[deal.stage],color:SC[deal.stage],border:`1px solid ${SC[deal.stage]}55`}}>{deal.stage}</span>
          {deal.type==='recurring'&&<span className="tag" style={{background:'#1e3a5f',color:'#60a5fa'}}>Recurring</span>}
          {openTasks.some(t=>isOverdue(t.due_date))&&<span className="tag" style={{background:'#450a0a',color:'#f87171'}}>⚠ Tasks overdue</span>}
          <span style={{fontSize:11,color:'#334155',marginLeft:'auto'}}>Added {deal.created_at?.slice(0,10)}</span>
        </div>
        <h1 style={{fontSize:22,color:'#f8fafc',lineHeight:1.2,marginBottom:8}}>{deal.name}</h1>
        <div style={{display:'flex',gap:16,alignItems:'baseline'}}>
          <span style={{fontSize:30,fontWeight:700,fontFamily:'Barlow',color:'#60a5fa'}}>{fmt(deal.value)}</span>
          {deal.type==='recurring'&&deal.mrr>0&&<span style={{fontSize:14,color:'#34d399'}}>{fmt(deal.mrr)}<span style={{fontSize:11,color:'#475569'}}>/mo</span></span>}
        </div>
      </div>
      <div style={{display:'flex',gap:3,marginBottom:20}}>
        {['Prospect','Qualified','Proposal','Negotiation','Closed Won'].map((s,i)=>(
          <div key={s} style={{flex:1,height:4,borderRadius:4,background:deal.stage==='Closed Lost'?(i===0?'#f87171':'#1e293b'):i<=idx?SC[deal.stage]:'#1e293b'}}/>
        ))}
      </div>
      <div className="card" style={{marginBottom:12}}>
        <span className="lbl">Contact</span>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{width:36,height:36,borderRadius:10,background:project?.color+'22',border:`1px solid ${project?.color}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,color:project?.color,flexShrink:0}}>{initials(deal.contact)}</div>
          <div style={{flex:1}}>
            <button onClick={()=>setShowEmail(!showEmail)} style={{background:'none',border:'none',color:'#e2e8f0',fontWeight:600,fontSize:14,cursor:'pointer',fontFamily:'inherit',padding:0,textAlign:'left'}}>{deal.contact||'—'}</button>
            <div style={{fontSize:12,color:'#64748b'}}>{deal.contact_title}</div>
            {showEmail&&deal.email&&<div style={{fontSize:12,color:'#60a5fa',marginTop:3}}>{deal.email}</div>}
          </div>
        </div>
      </div>
      <div className="card" style={{marginBottom:12}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
          <span className="lbl" style={{marginBottom:0}}>Tasks <span style={{color:'#475569',fontWeight:400,textTransform:'none',letterSpacing:'normal',fontSize:11}}>({openTasks.length} open)</span></span>
          <button onClick={getCoaching} style={{background:'#1e3a5f',border:'1px solid #3b82f655',color:'#93c5fd',borderRadius:6,padding:'3px 10px',fontSize:11,cursor:'pointer',fontFamily:'inherit',fontWeight:600}}>
            {coachLoading?'Thinking…':'AI Coach'}
          </button>
        </div>
        {coaching&&<div style={{marginBottom:12,padding:'10px 12px',background:'#0a0f1a',borderRadius:10,border:'1px solid #1e293b',fontSize:13,color:'#94a3b8',lineHeight:1.7,whiteSpace:'pre-wrap'}}><div style={{fontSize:10,color:'#60a5fa',fontWeight:700,marginBottom:5}}>COACHING</div>{coaching}</div>}
        {openTasks.map(t=><SwipeTask key={t.id} task={t} onComplete={()=>onCompleteTask(t.id)} onDelete={()=>onDeleteTask(t.id)}/>)}
        <div style={{marginTop:10,display:'flex',flexDirection:'column',gap:8}}>
          <input type="text" value={taskText} onChange={e=>setTaskText(e.target.value)} placeholder="Add task…" style={{fontSize:13,padding:'9px 12px'}} onKeyDown={e=>{if(e.key==='Enter'&&taskText.trim()){onAddTask(taskText,taskDate);setTaskText('');setTaskDate('');}}}/>
          <div style={{display:'flex',gap:8}}>
            <input type="date" value={taskDate} onChange={e=>setTaskDate(e.target.value)} style={{flex:1,fontSize:13,padding:'9px 12px'}}/>
            <button onClick={()=>{if(taskText.trim()){onAddTask(taskText,taskDate);setTaskText('');setTaskDate('');}}} style={{background:'#1e3a5f',border:'1px solid #3b82f655',color:'#60a5fa',borderRadius:10,padding:'9px 14px',cursor:'pointer',fontSize:13,fontFamily:'inherit',whiteSpace:'nowrap'}}>Add</button>
          </div>
        </div>
        {doneTasks.length>0&&(
          <div style={{marginTop:12,borderTop:'1px solid #1e293b',paddingTop:10}}>
            <button onClick={()=>setShowDone(!showDone)} style={{background:'none',border:'none',color:'#475569',fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>
              {showDone?'▾':'▸'} {doneTasks.length} completed
            </button>
            {showDone&&doneTasks.map(t=>(
              <div key={t.id} style={{fontSize:13,color:'#334155',textDecoration:'line-through',padding:'5px 0',display:'flex',alignItems:'center',gap:8}}>
                <span style={{color:'#34d399',fontSize:11}}>✓</span>{t.text}
              </div>
            ))}
          </div>
        )}
      </div>
      {deal.notes&&<div className="card" style={{marginBottom:12}}><span className="lbl">Notes</span><div style={{fontSize:14,color:'#94a3b8',lineHeight:1.6}}>{deal.notes}</div></div>}
      {deal.context&&<div className="card" style={{marginBottom:12,borderColor:'#2e1f5e'}}><span className="lbl" style={{color:'#a78bfa'}}>Context & Strategy</span><div style={{fontSize:14,color:'#94a3b8',lineHeight:1.7,whiteSpace:'pre-wrap'}}>{deal.context}</div></div>}
      <div className="card" style={{marginBottom:20}}>
        <span className="lbl">Activity Log</span>
        <div style={{display:'flex',gap:8,marginBottom:12}}>
          <input type="text" value={actText} onChange={e=>setActText(e.target.value)} placeholder="Log a call, email, update…" style={{fontSize:13,padding:'9px 12px'}} onKeyDown={e=>{if(e.key==='Enter'&&actText.trim()){onAddActivity(actText);setActText('');}}}/>
          <button onClick={()=>{if(actText.trim()){onAddActivity(actText);setActText('');}}} style={{background:'#1e3a5f',border:'1px solid #3b82f655',color:'#60a5fa',borderRadius:10,padding:'9px 14px',cursor:'pointer',fontSize:13,fontFamily:'inherit',whiteSpace:'nowrap'}}>Log</button>
        </div>
        {!activity.length&&<div style={{fontSize:13,color:'#334155'}}>No activity yet.</div>}
        {activity.map(a=>(
          <div key={a.id} style={{display:'flex',gap:10,marginBottom:8,paddingBottom:8,borderBottom:'1px solid #1e293b'}}>
            <div style={{width:6,height:6,borderRadius:'50%',background:a.is_system?'#34d399':'#3b82f6',marginTop:6,flexShrink:0}}/>
            <div><div style={{fontSize:13,color:a.is_system?'#64748b':'#94a3b8',lineHeight:1.5}}>{a.text}</div><div style={{fontSize:11,color:'#334155',marginTop:2}}>{a.created_at?.slice(0,10)}</div></div>
          </div>
        ))}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        <button className="bp" onClick={onEdit}>Edit Deal</button>
        <button className="bd" onClick={onDelete}>Delete Deal</button>
      </div>
    </div>
  );
}

// ─── SWIPE TASK ───────────────────────────────────────────────────────────────
function SwipeTask({task,onComplete,onDelete}) {
  const [x,setX]=useState(0);const [sw,setSw]=useState(false);const [done,setDone]=useState(false);
  const sx=useRef(null);const THRESH=70;const overdue=isOverdue(task.due_date);
  function ts(e){sx.current=e.touches[0].clientX;setSw(true);}
  function tm(e){if(sx.current===null)return;const dx=e.touches[0].clientX-sx.current;if(dx<0)setX(Math.max(dx,-120));}
  function te(){if(x<-THRESH)complete();else setX(0);setSw(false);sx.current=null;}
  function ms(e){sx.current=e.clientX;setSw(true);}
  function mm(e){if(!sw||sx.current===null)return;const dx=e.clientX-sx.current;if(dx<0)setX(Math.max(dx,-120));}
  function mu(){if(x<-THRESH)complete();else setX(0);setSw(false);sx.current=null;}
  function complete(){setDone(true);setTimeout(onComplete,350);}
  const prog=Math.min(Math.abs(x)/THRESH,1);
  return(
    <div style={{position:'relative',marginBottom:8,borderRadius:10,overflow:'hidden',userSelect:'none'}} onMouseMove={mm} onMouseUp={mu} onMouseLeave={mu}>
      <div style={{position:'absolute',right:0,top:0,bottom:0,width:Math.min(Math.abs(x),120),background:`rgba(52,211,153,${0.1+prog*0.3})`,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:'0 10px 10px 0'}}>
        <svg width="18" height="18" fill="none" stroke="#34d399" strokeWidth="2.5" viewBox="0 0 24 24" style={{opacity:prog}}><path d="M5 13l4 4L19 7"/></svg>
      </div>
      <div style={{transform:`translateX(${done?-160:x}px)`,opacity:done?0:1,transition:sw?'none':'transform 0.3s,opacity 0.3s',background:'#1a2540',borderRadius:10,padding:'10px 12px',display:'flex',alignItems:'center',gap:10,cursor:'grab'}}
        onTouchStart={ts} onTouchMove={tm} onTouchEnd={te} onMouseDown={ms}>
        <div style={{flex:1}}>
          <div style={{fontSize:13,color:'#cbd5e1',fontWeight:500}}>{task.text}</div>
          {task.due_date&&<div style={{fontSize:11,color:overdue?'#f87171':'#64748b',marginTop:2}}>{overdue?'Overdue · ':''}{task.due_date}</div>}
        </div>
        <button onClick={e=>{e.stopPropagation();onDelete();}} style={{background:'none',border:'none',color:'#334155',cursor:'pointer',fontSize:16,padding:'2px 4px',flexShrink:0}}>✕</button>
      </div>
    </div>
  );
}

// ─── ACTIONS PANEL ────────────────────────────────────────────────────────────
function ActionsPanel({deals,projects,tasks,activity,onCompleteTask,onSelectDeal,onAddTask}) {
  const [timeframe,setTimeframe]=useState('today');
  const [advice,setAdvice]=useState({});
  const [loading,setLoading]=useState({});
  const [doneIds,setDoneIds]=useState([]);
  const today=todayStr(); const we=weekEnd();

  const allOpenTasks = tasks.filter(t=>!t.done&&!doneIds.includes(t.id)).map(t=>{
    const deal=deals.find(d=>d.id===t.deal_id);
    const proj=deal?projects.find(p=>p.id===deal.project_id):null;
    return {...t,deal,proj};
  }).filter(t=>t.deal);

  const filtered = timeframe==='today'
    ? allOpenTasks.filter(t=>t.due_date===today||isOverdue(t.due_date)||!t.due_date)
    : allOpenTasks;

  const sorted = [...filtered].sort((a,b)=>{
    const pa=isOverdue(a.due_date)?0:a.due_date===today?1:a.due_date?2:3;
    const pb=isOverdue(b.due_date)?0:b.due_date===today?1:b.due_date?2:3;
    return pa-pb||(Number(b.deal?.value)||0)-(Number(a.deal?.value)||0);
  });

  const overdueItems=allOpenTasks.filter(t=>isOverdue(t.due_date));
  const doneToday=activity.filter(a=>a.created_at?.slice(0,10)===today&&a.is_system);

  async function coach(task) {
    setLoading(l=>({...l,[task.id]:true}));
    const r=await askClaude(
      `Deal: "${task.deal?.name}" | Stage: ${task.deal?.stage} | Task: "${task.text}" | Due: ${task.due_date||'none'} | Notes: ${task.deal?.notes||'none'}\nHow should I approach completing this task? One sentence.`,
      'You are a sales coach. One sentence. Concrete and direct.'
    );
    setAdvice(a=>({...a,[task.id]:r}));
    setLoading(l=>({...l,[task.id]:false}));
  }

  function handleComplete(task) {
    setDoneIds(ids=>[...ids,task.id]);
    onCompleteTask(task.deal_id,task.id);
  }

  const groups=[
    {label:'OVERDUE',color:'#f87171',items:sorted.filter(t=>isOverdue(t.due_date))},
    {label:'TODAY',color:'#f59e0b',items:sorted.filter(t=>t.due_date===today)},
    {label:timeframe==='week'?'THIS WEEK':'UPCOMING',color:'#60a5fa',items:sorted.filter(t=>!isOverdue(t.due_date)&&t.due_date!==today&&t.due_date&&t.due_date<=we)},
    {label:'NO DATE',color:'#64748b',items:sorted.filter(t=>!t.due_date)},
  ].filter(g=>g.items.length);

  return(
    <div className="fi" style={{padding:'16px 18px',maxWidth:640,margin:'0 auto'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
        <h1 style={{fontSize:24,color:'#f8fafc'}}>Actions</h1>
        <div className="toggle-pill">
          <button className={`toggle-btn ${timeframe==='today'?'on':''}`} onClick={()=>setTimeframe('today')}>Today</button>
          <button className={`toggle-btn ${timeframe==='week'?'on':''}`} onClick={()=>setTimeframe('week')}>This Week</button>
        </div>
      </div>
      <div style={{fontSize:13,color:'#64748b',marginBottom:18}}>
        {timeframe==='today'?`${overdueItems.length>0?overdueItems.length+' overdue · ':''}${allOpenTasks.filter(t=>t.due_date===today).length} due today`:`${sorted.length} tasks this week`} · swipe left to complete
      </div>
      {doneToday.length>0&&(
        <div style={{background:'#064e3b',border:'1px solid #34d39933',borderRadius:12,padding:'12px 16px',marginBottom:16}}>
          <div style={{fontSize:11,color:'#34d399',fontWeight:700,marginBottom:6}}>✓ DONE TODAY — {doneToday.length}</div>
          {doneToday.map((a,i)=><div key={i} style={{fontSize:12,color:'#6ee7b7',padding:'2px 0'}}>· {a.text.replace('✓ Done: ','')}</div>)}
        </div>
      )}
      {!sorted.length&&(
        <div style={{padding:'48px 0',textAlign:'center',color:'#475569'}}>
          <div style={{fontSize:36,marginBottom:10}}>✓</div>
          <h2 style={{fontSize:16,color:'#64748b',marginBottom:4}}>All clear</h2>
          <div style={{fontSize:13,color:'#334155'}}>No pending actions {timeframe==='today'?'for today':'this week'}</div>
        </div>
      )}
      {groups.map(g=>(
        <div key={g.label} style={{marginBottom:6}}>
          <div style={{fontSize:10,color:g.color,fontWeight:700,letterSpacing:'0.1em',marginBottom:8,marginTop:6}}>{g.label}</div>
          {g.items.map(t=>(
            <ActionTaskCard key={t.id} task={t} proj={t.proj}
              onComplete={()=>handleComplete(t)}
              onSelectDeal={()=>onSelectDeal(t.deal)}
              onCoach={()=>coach(t)}
              advice={advice[t.id]}
              coachLoading={loading[t.id]}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── ACTION TASK CARD ─────────────────────────────────────────────────────────
function ActionTaskCard({task,proj,onComplete,onSelectDeal,onCoach,advice,coachLoading}) {
  const [x,setX]=useState(0);const [sw,setSw]=useState(false);const [gone,setGone]=useState(false);
  const sx=useRef(null);const THRESH=80;const overdue=isOverdue(task.due_date);
  function ts(e){sx.current=e.touches[0].clientX;setSw(true);}
  function tm(e){if(sx.current===null)return;const dx=e.touches[0].clientX-sx.current;if(dx<0)setX(Math.max(dx,-140));}
  function te(){if(x<-THRESH)complete();else setX(0);setSw(false);sx.current=null;}
  function ms(e){sx.current=e.clientX;setSw(true);}
  function mm(e){if(!sw||sx.current===null)return;const dx=e.clientX-sx.current;if(dx<0)setX(Math.max(dx,-140));}
  function mu(){if(x<-THRESH)complete();else setX(0);setSw(false);sx.current=null;}
  function complete(){setGone(true);setTimeout(onComplete,350);}
  const prog=Math.min(Math.abs(x)/THRESH,1);
  if(gone) return null;
  return(
    <div style={{position:'relative',marginBottom:10,borderRadius:14,overflow:'hidden',userSelect:'none'}} onMouseMove={mm} onMouseUp={mu} onMouseLeave={mu}>
      <div style={{position:'absolute',right:0,top:0,bottom:0,width:Math.min(Math.abs(x),140),background:`rgba(52,211,153,${0.12+prog*0.28})`,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:4,borderRadius:'0 14px 14px 0'}}>
        <svg width="20" height="20" fill="none" stroke="#34d399" strokeWidth="2.5" viewBox="0 0 24 24" style={{opacity:prog}}><path d="M5 13l4 4L19 7"/></svg>
        {prog>=0.9&&<span style={{fontSize:9,color:'#34d399',fontWeight:700}}>DONE!</span>}
      </div>
      <div style={{transform:`translateX(${gone?-200:x}px)`,opacity:gone?0:1,transition:sw?'none':'transform 0.3s,opacity 0.3s',background:'#111827',border:`1px solid ${overdue?'#f8717133':'#1e293b'}`,borderRadius:14,padding:'14px 16px',cursor:'grab',position:'relative',zIndex:1}}
        onTouchStart={ts} onTouchMove={tm} onTouchEnd={te} onMouseDown={ms}>
        <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
          <div style={{flex:1,minWidth:0}} onClick={onSelectDeal}>
            <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:6}}>
              {proj&&<span className="tag" style={{background:proj.color+'22',color:proj.color}}>{proj.emoji} {proj.name}</span>}
              {task.deal&&<span className="tag" style={{background:SB[task.deal.stage],color:SC[task.deal.stage]}}>{task.deal.stage}</span>}
              {overdue&&<span className="tag" style={{background:'#450a0a',color:'#f87171'}}>overdue</span>}
            </div>
            {task.deal&&<div style={{fontSize:11,color:'#64748b',marginBottom:3}}>{task.deal.name}</div>}
            <div style={{fontSize:14,fontWeight:600,color:'#f1f5f9',marginBottom:task.due_date?4:0}}>{task.text}</div>
            {task.due_date&&<div style={{fontSize:11,color:overdue?'#f87171':'#64748b'}}>Due {task.due_date}</div>}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6,alignItems:'flex-end',flexShrink:0}}>
            <button onClick={e=>{e.stopPropagation();onCoach();}} style={{background:'#1e293b',border:'1px solid #334155',color:'#94a3b8',borderRadius:8,padding:'5px 10px',fontSize:11,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>
              {coachLoading?'…':'Coach'}
            </button>
            <div style={{fontSize:10,color:'#334155'}}>← swipe</div>
          </div>
        </div>
        {advice&&<div style={{marginTop:10,padding:'10px 12px',background:'#0f1f3d',border:'1px solid #1e3a5f',borderRadius:10,fontSize:13,color:'#93c5fd',lineHeight:1.5}}>💡 {advice}</div>}
      </div>
    </div>
  );
}

// ─── DIGEST PANEL ─────────────────────────────────────────────────────────────
function DigestPanel({deals,projects,tasks}) {
  const [digest,setDigest]=useState('');const [loading,setLoading]=useState(false);
  async function generate() {
    setLoading(true);setDigest('');
    const summary=projects.map(p=>{
      const pd=deals.filter(d=>d.project_id===p.id);
      const pipeline=pd.reduce((a,b)=>a+(Number(b.value)||0),0);
      const mrr=pd.filter(d=>d.type==='recurring').reduce((a,b)=>a+(Number(b.mrr)||0),0);
      const openTasks=tasks.filter(t=>!t.done&&pd.find(d=>d.id===t.deal_id));
      const taskList=openTasks.map(t=>{const d=pd.find(x=>x.id===t.deal_id);return `${d?.name}: ${t.text}${t.due_date?` (due ${t.due_date})`:''}`; });
      return `PROJECT: ${p.name}\nPipeline: $${pipeline.toLocaleString()} | MRR: $${mrr}/mo\nOpen tasks: ${taskList.join('; ')||'none'}\nStrategy: ${p.strategy||'not set'}`;
    }).join('\n\n');
    const r=await askClaude(`My pipeline:\n\n${summary}\n\nWeekly digest: overall health, top 3 priorities, deals at risk, one strategic recommendation.`,
      'Sharp business advisor. Concise weekly digest. ALL CAPS section titles. No markdown. Plain text. Direct.');
    setDigest(r);setLoading(false);
  }
  useEffect(()=>{generate();},[]);
  return(
    <div className="fi" style={{padding:'16px 18px',maxWidth:640,margin:'0 auto'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
        <h1 style={{fontSize:24,color:'#f8fafc'}}>Weekly Digest</h1>
        <button onClick={generate} style={{background:'#1e3a5f',border:'1px solid #3b82f655',color:'#60a5fa',borderRadius:8,padding:'7px 14px',fontSize:12,cursor:'pointer',fontFamily:'inherit',fontWeight:600}}>Refresh</button>
      </div>
      {loading?<div style={{color:'#60a5fa',fontSize:14,padding:30,textAlign:'center'}}>Generating…</div>
        :<div className="card" style={{fontSize:14,color:'#94a3b8',lineHeight:1.9,whiteSpace:'pre-wrap'}}>{digest}</div>}
    </div>
  );
}

// ─── PROJECT FORM ─────────────────────────────────────────────────────────────
function ProjectForm({form,setForm,isEdit,onSave,onCancel,onDelete,activeProject,onAddMilestone,onToggleMilestone,onDeleteMilestone}) {
  const [newMs,setNewMs]=useState('');
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));
  return(
    <div style={{padding:'16px 18px',maxWidth:580,margin:'0 auto'}}>
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <div><span className="lbl">Project Name *</span><input type="text" value={form.name} onChange={e=>f('name',e.target.value)} placeholder="e.g. Escaneate"/></div>
        <div><span className="lbl">Description</span><input type="text" value={form.description||''} onChange={e=>f('description',e.target.value)} placeholder="One line summary"/></div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div><span className="lbl">Color</span><div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:6}}>{PC.map(c=><button key={c} onClick={()=>f('color',c)} style={{width:28,height:28,borderRadius:'50%',background:c,border:`2px solid ${form.color===c?'#fff':'transparent'}`,cursor:'pointer'}}/>)}</div></div>
          <div><span className="lbl">Emoji</span><div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:4}}>{PE.map(e=><button key={e} onClick={()=>f('emoji',e)} style={{fontSize:20,background:form.emoji===e?'#1e293b':'transparent',border:`1px solid ${form.emoji===e?'#3b82f6':'transparent'}`,borderRadius:8,padding:'4px 6px',cursor:'pointer'}}>{e}</button>)}</div></div>
        </div>
        <div><span className="lbl">Strategy & Context</span><textarea value={form.strategy||''} onChange={e=>f('strategy',e.target.value)} placeholder="Overall strategy, goals, key decisions, context…" style={{minHeight:120}}/></div>
        {isEdit&&activeProject&&(
          <div>
            <span className="lbl">Milestones</span>
            {(activeProject.milestones||[]).map(m=>(
              <div key={m.id} style={{display:'flex',alignItems:'center',gap:10,marginBottom:8,padding:'10px 12px',background:'#111827',borderRadius:10}}>
                <input type="checkbox" checked={m.done} onChange={()=>onToggleMilestone(m.id)} style={{width:15,height:15,cursor:'pointer',accentColor:activeProject.color}}/>
                <span style={{flex:1,fontSize:13,color:m.done?'#475569':'#94a3b8',textDecoration:m.done?'line-through':'none'}}>{m.text}</span>
                <button onClick={()=>onDeleteMilestone(m.id)} style={{background:'none',border:'none',color:'#475569',cursor:'pointer',fontSize:14}}>✕</button>
              </div>
            ))}
            <div style={{display:'flex',gap:8,marginTop:4}}>
              <input type="text" value={newMs} onChange={e=>setNewMs(e.target.value)} placeholder="Add milestone…" style={{fontSize:13,padding:'10px 12px'}} onKeyDown={e=>{if(e.key==='Enter'&&newMs.trim()){onAddMilestone(newMs);setNewMs('');}}}/>
              <button onClick={()=>{if(newMs.trim()){onAddMilestone(newMs);setNewMs('');}}} style={{background:'#1e3a5f',border:'1px solid #3b82f655',color:'#60a5fa',borderRadius:10,padding:'10px 16px',cursor:'pointer',fontSize:13,fontFamily:'inherit',whiteSpace:'nowrap'}}>Add</button>
            </div>
          </div>
        )}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:24}}>
        <button className="bp" onClick={onSave}>{isEdit?'Save Project':'Create Project'}</button>
        <button className="bg" onClick={onCancel}>Cancel</button>
        {isEdit&&onDelete&&<button className="bd" onClick={onDelete}>Delete Project & All Deals</button>}
      </div>
    </div>
  );
}

// ─── DEAL FORM ────────────────────────────────────────────────────────────────
function DealForm({form,setForm}) {
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));
  return(
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <div><span className="lbl">Deal Name *</span><input type="text" value={form.name} onChange={e=>f('name',e.target.value)} placeholder="e.g. Series A Lead"/></div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        <div><span className="lbl">Stage</span><select value={form.stage} onChange={e=>f('stage',e.target.value)}>{STAGES.map(s=><option key={s}>{s}</option>)}</select></div>
        <div><span className="lbl">Deal Type</span><select value={form.type} onChange={e=>f('type',e.target.value)}><option value="one-off">One-off</option><option value="recurring">Recurring</option></select></div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:form.type==='recurring'?'1fr 1fr':'1fr',gap:12}}>
        <div><span className="lbl">Total Value ($)</span><input type="number" value={form.value} onChange={e=>f('value',e.target.value)} placeholder="0"/></div>
        {form.type==='recurring'&&<div><span className="lbl">Monthly Value ($)</span><input type="number" value={form.mrr} onChange={e=>f('mrr',e.target.value)} placeholder="0"/></div>}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        <div><span className="lbl">Contact Name</span><input type="text" value={form.contact||''} onChange={e=>f('contact',e.target.value)} placeholder="Full name"/></div>
        <div><span className="lbl">Title</span><input type="text" value={form.contact_title||''} onChange={e=>f('contact_title',e.target.value)} placeholder="VP Sales"/></div>
      </div>
      <div><span className="lbl">Email</span><input type="email" value={form.email||''} onChange={e=>f('email',e.target.value)} placeholder="contact@company.com"/></div>
      <div><span className="lbl">Notes</span><textarea value={form.notes||''} onChange={e=>f('notes',e.target.value)} placeholder="Key deal context, requirements, blockers…"/></div>
      <div><span className="lbl">Context & Strategy</span><textarea value={form.context||''} onChange={e=>f('context',e.target.value)} placeholder="Strategy notes, decision history, relationship context…" style={{minHeight:100}}/></div>
    </div>
  );
}
